import {
  createTheme as muiCreateTheme,
  responsiveFontSizes,
  alpha,
} from "@mui/material/styles";
import { resolveBrand } from "@/shared/constants/brand";

type ThemeMode = "light" | "dark";

// Палитра идёт от бренда сборки (VITE_BRAND): у adult-инстанса кабинет
// розовый, как его знак, без переменной остаётся оранжевый ottkit.
// Литералы цветов лежат в одном месте, shared/constants/brand.ts.
const brand = resolveBrand(import.meta.env.VITE_BRAND);

export const PRIMARY_MAIN = brand.colors.primary;
const PRIMARY_CONTRAST_TEXT = brand.colors.contrastText;
const SECONDARY_MAIN = brand.colors.secondary;

export function createTheme(mode: ThemeMode) {
  const themedParam = <T extends string | number>(light: T, dark: T): T =>
    mode === "dark" ? dark : light;

  const baseTheme = {
    palette: {
      mode,
      primary: {
        main: PRIMARY_MAIN,
        contrastText: PRIMARY_CONTRAST_TEXT,
      },
      secondary: {
        main: SECONDARY_MAIN,
      },
      background: {
        default: themedParam("#f5f5f5", "#131313"),
        secondary: themedParam("#f5f5f5", "#131313"),
        paper: themedParam("#fff", "#1f1f1f"),
        selected: alpha(PRIMARY_MAIN, 0.16),
      },
    },

    typography: {
      h5: {
        fontWeight: 500,
      },
      h6: {
        fontWeight: 500,
      },
      // Подписи кнопок, вкладок и переключателей обычным регистром, как в
      // переводах: заглавные по умолчанию MUI кричат и хуже читаются.
      button: {
        textTransform: "none" as const,
      },
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
    },

    components: {
      MuiAutocomplete: {
        defaultProps: {
          getOptionKey: (option: { value?: string }) => option?.value ?? "",
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: {
            "&::before": {
              backgroundColor: themedParam("#f5f5f5", "#131313"),
            },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            whiteSpace: "nowrap",
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiLink: {
        defaultProps: {
          underline: "hover" as const,
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            paddingBottom: 16,
            "&:last-child": {
              paddingBottom: 16,
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            zIndex: 1000,
            msOverflowStyle: "none" as const,
            scrollbarWidth: "none" as const,
            "&::-webkit-scrollbar": {
              display: "none",
            },
          } as any,
          paperAnchorDockedLeft: {
            borderRight: 0,
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: themedParam(
                "rgba(0, 0, 0, 0.03)",
                "rgba(255, 255, 255, 0.16)"
              ),
              "&:hover": {
                backgroundColor: themedParam(
                  "rgba(0, 0, 0, 0.03)",
                  "rgba(255, 255, 255, 0.20)"
                ),
              },
            },
          },
        },
      },

      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            color: "white",
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          focusHighlight: {
            backgroundColor: PRIMARY_MAIN,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textDecoration: "none!important",
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            scrollbarWidth: "none" as const,
          } as any,
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: alpha(PRIMARY_MAIN, themedParam(0.12, 0.28)),
              "&:hover": {
                backgroundColor: alpha(PRIMARY_MAIN, themedParam(0.12, 0.28)),
              },
            },
          },
          hover: {
            cursor: "pointer",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "&:focus": {
              background: "none",
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid ",
            borderColor: themedParam("#f5f5f5", "#141414"),
          },
          head: {
            color: themedParam(
              "rgba(0, 0, 0, 0.7)",
              "rgba(255, 255, 255, 0.7)"
            ),
          },
          sizeSmall: {
            padding: "6px 16px 6px 16px",
          },
        },
      },

      MuiDialog: {
        defaultProps: {
          PaperProps: {
            elevation: 0,
          },
        },
        styleOverrides: {
          paper: {
            backgroundColor: themedParam("#f5f5f5", "#131313"),
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: themedParam("#f5f5f5", "#141414"),
          },
        },
      },

      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: themedParam(
              "rgba(0, 0, 0, 0.05)",
              "rgba(255, 255, 255, 0.05)"
            ),
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: themedParam("#fff", "#191919"),
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: {
          a: {
            color: PRIMARY_MAIN,
            textDecoration: "none",
          },

          body: {
            margin: 0,
            padding: 0,
            height: "100%",
            WebkitTextSizeAdjust: "100%",
          },
          html: {
            margin: 0,
            padding: 0,
            height: "100%",
          },
          "& #root": {
            minHeight: "100%",
            height: "100%",
          },
          "& .mdxeditor-popup-container": {
            zIndex: "20000 !important",
            "& .mdxeditor-popup": {
              backgroundColor: themedParam("#ffffff", "#1f1f1f"),
              color: themedParam("rgba(0, 0, 0, 0.87)", "#ffffff"),
              border: `1px solid ${themedParam(
                "rgba(0, 0, 0, 0.12)",
                "#333333"
              )}`,
              boxShadow: themedParam(
                "0 4px 12px rgba(0, 0, 0, 0.12)",
                "0 4px 12px rgba(0, 0, 0, 0.4)"
              ),
            },
            "& .mdxeditor-popup input, & .mdxeditor-popup textarea, & .mdxeditor-popup select":
              {
                backgroundColor: themedParam("#ffffff", "#1f1f1f"),
                color: themedParam("rgba(0, 0, 0, 0.87)", "#ffffff"),
                border: `1px solid ${themedParam(
                  "rgba(0, 0, 0, 0.23)",
                  "#3d3d3d"
                )}`,
                borderRadius: 4,
              },
            "& .mdxeditor-popup button": {
              color: themedParam("rgba(0, 0, 0, 0.87)", "#ffffff"),
            },
          },
        },
      },
    },
  };

  const theme = muiCreateTheme(baseTheme);

  return responsiveFontSizes(theme);
}
